/**
 * @fileoverview Case Ingestion Page - Full-screen wizard for creating and populating legal matters.
 *
 * This is the main entry point for the unified case ingestion flow, combining matter creation
 * with a 10-step data entry wizard. The flow consists of:
 * - Step 0 (Basics): Firm selection, client name, case type -> creates matter
 * - Step 1 (Case Info): Attorney, dates, location -> patches matter
 * - Step 2 (Client): Client contact and demographic information
 * - Step 3 (Companions): Link to companion case groups
 * - Steps 4-8: Police, treatment, coverage, documents, notes
 * - Step 9 (Review): Summary of collected data
 *
 * Routes:
 * - `/matters/ingestion` -- Start fresh (creates new matter)
 * - `/matters/:matterId/ingestion` -- Resume existing matter
 *
 * Layout: sidebar (w-52) | scrollable content (flex-1) | right assist sidebar (w-80)
 *
 * @see docs/CASE_INGESTION.md for architecture details
 */
import React, { useCallback, useMemo, useRef } from 'react';

import { DuplicateSuggestions } from '@/components/matter-form/DuplicateSuggestions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useGetMatterDetailsQuery, useGetMatterServicesQuery } from '@/services/api/mattersService';
import type { SearchResult } from '@/services/api/searchService';

import { AssistSidebarPanel } from './assist/AssistSidebarPanel';
import { UNIFIED_STEPS } from './constants';
import { CaseSetupMessagingProvider } from './context/CaseSetupMessagingContext';
import {
  UnifiedFlowProvider,
  useUnifiedFlow,
} from './context/UnifiedFlowContext';
import {
  ContentNavigation,
  IngestionStepper,
  MatterTitle,
} from './navigation';
import { IngestionStepSkeleton } from './skeletons';
import {
  BasicsStep,
  CaseInfoStep,
  CaseSummaryStep,
  ClientInformationStep,
  CompanionCasesStep,
  CoverageStep,
  DocumentsStep,
  LiabilityPoliceReportStep,
  ReviewStep,
  TreatmentStep,
} from './steps';

/**
 * Page shell: 3-column layout.
 * Left sidebar (w-52) | scrollable form content (flex-1) | right assist sidebar (w-80)
 * Uses Stripe-inspired design: subtle gradients, refined borders, polished shadows.
 */
const PageShell: React.FC<{
  showFinish?: boolean;
  children: React.ReactNode;
}> = ({ showFinish, children }) => {
  const {
    state,
    isAssistPanelOpen,
    setIsAssistPanelOpen,
    assistState,
  } = useUnifiedFlow();
  const { currentStepIndex } = state;
  const currentStepId = UNIFIED_STEPS[currentStepIndex]?.id;

  return (
    <div className="fixed inset-0 bg-zinc-50 dark:bg-zinc-950 overflow-hidden flex flex-col">
      {/* Full-width matter title bar */}
      <MatterTitle />

      {/* Main area: left sidebar + content + right assist sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Stripe style: subtle gradient + border */}
        <aside className="m-4 w-52 rounded-xl bg-gradient-to-b from-white to-zinc-100 border border-zinc-200 dark:from-zinc-900 dark:to-zinc-800 dark:border-zinc-700 flex-shrink-0 overflow-y-auto">
          <IngestionStepper />
        </aside>

        {/* Content - scrollable */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-4 space-y-4">
            {/* Navigation header - Stripe style: soft gradient + refined shadow */}
            <div className="rounded-xl bg-gradient-to-r from-white to-zinc-50 border border-zinc-200 px-4 py-3 shadow-sm dark:from-zinc-900 dark:to-zinc-800 dark:border-zinc-700">
              <ContentNavigation showFinish={showFinish} />
            </div>
            {/* Form content */}
            {children}
          </div>
        </main>

        {/* Right assist sidebar */}
        <AssistSidebarPanel
          assistState={assistState}
          isOpen={isAssistPanelOpen}
          onClose={() => setIsAssistPanelOpen(false)}
          currentStepId={currentStepId}
        />
      </div>
    </div>
  );
};

/**
 * Dialog shown when duplicates are detected during matter creation.
 * Allows user to review potential duplicates and choose to proceed or cancel.
 */
const DuplicateConfirmationDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicates: SearchResult[];
  onConfirm: () => void;
}> = ({ open, onOpenChange, duplicates, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Potential Duplicate Detected</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-4">
            <p>
              We found {duplicates.length} similar{' '}
              {duplicates.length === 1 ? 'matter or intake' : 'matters or intakes'}.
              Please review before creating a new matter.
            </p>
            <DuplicateSuggestions suggestions={duplicates} />
            <p className="text-sm">Are you sure you want to create a new matter?</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>Create Anyway</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Renders the creation-phase step (Type toggle + Basics merged).
 */
const CreationStepContent: React.FC = () => {
  const { creationForm, selectedFirm, setSelectedFirm, state } = useUnifiedFlow();
  const { isCreatingMatter, matterId } = state;

  const {
    control,
    setValue,
    formState: { errors },
  } = creationForm;

  return (
    <BasicsStep
      control={control}
      errors={errors}
      selectedFirm={selectedFirm}
      setSelectedFirm={setSelectedFirm}
      setValue={setValue}
      isLoading={isCreatingMatter}
      matterExists={!!matterId}
    />
  );
};

/**
 * Renders the current ingestion step.
 * Handles both Case Info (non-audit-tracked) and audit-tracked steps.
 */
const IngestionStepContent: React.FC<{ matterId: string; firmId: string }> = ({
  matterId,
  firmId,
}) => {
  const { state: unifiedState, creationForm, onSaveComplete, getSectionStatuses } = useUnifiedFlow();

  const currentUnifiedStep = UNIFIED_STEPS[unifiedState.currentStepIndex];

  // Wait for audit record to load before rendering steps
  // This ensures sectionStatuses is available for proper state initialization
  if (unifiedState.isLoadingAudit) {
    return <IngestionStepSkeleton />;
  }

  // Get section statuses for passing to step components
  const sectionStatuses = getSectionStatuses();

  // Case Info step (non-audit-tracked) -- renders GuidedDetailsStep
  if (currentUnifiedStep?.id === 'case_info') {
    const {
      control,
      setValue,
      formState: { errors },
    } = creationForm;

    return (
      <CaseInfoStep
        control={control}
        errors={errors}
        firmId={firmId}
        setValue={setValue}
        isLoading={unifiedState.isPatchingCaseInfo}
      />
    );
  }

  // Review step -- shows summary of collected vs missing data
  if (currentUnifiedStep?.id === 'review') {
    return <ReviewStep />;
  }

  // Render step based on step ID
  const stepId = currentUnifiedStep?.id;

  switch (stepId) {
    case 'client_info':
      return (
        <ClientInformationStep
          matterId={matterId}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'companion_cases':
      return (
        <CompanionCasesStep
          matterId={matterId}
          sectionStatuses={sectionStatuses}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'liability':
      return (
        <LiabilityPoliceReportStep
          matterId={matterId}
          sectionStatuses={sectionStatuses}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'treatment':
      return (
        <TreatmentStep
          matterId={matterId}
          sectionStatuses={sectionStatuses}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'coverage':
      return (
        <CoverageStep
          matterId={matterId}
          sectionStatuses={sectionStatuses}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'documents':
      return (
        <DocumentsStep
          matterId={matterId}
          sectionStatuses={sectionStatuses}
          onSaveComplete={onSaveComplete}
        />
      );
    case 'case_summary':
      return (
        <CaseSummaryStep
          matterId={matterId}
          onSaveComplete={onSaveComplete}
        />
      );
    default:
      return null;
  }
};

/**
 * Main content with two-phase rendering.
 * Audit record is fetched by UnifiedFlowProvider when matterId exists.
 */
const UnifiedFlowContent: React.FC = () => {
  const {
    state,
    showDuplicateDialog,
    setShowDuplicateDialog,
    handleConfirmCreateAnyway,
    duplicates,
    goNext,
  } = useUnifiedFlow();
  const { phase, matterId } = state;

  const isConfirmingRef = useRef(false);

  const onConfirmCreateAnyway = useCallback(async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    try {
      await handleConfirmCreateAnyway();
      await goNext();
    } finally {
      isConfirmingRef.current = false;
    }
  }, [handleConfirmCreateAnyway, goNext]);

  // Content varies based on whether matter exists
  const content = matterId ? (
    <MatterExistsLayout matterId={matterId} phase={phase} />
  ) : (
    <PageShell>
      <CreationStepContent />
    </PageShell>
  );

  return (
    <>
      {content}
      <DuplicateConfirmationDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        duplicates={duplicates}
        onConfirm={onConfirmCreateAnyway}
      />
    </>
  );
};

/**
 * Layout when matter exists - shows stepper with completion data regardless of current step.
 * Wraps the Review step in CaseSetupMessagingProvider so the messaging panel
 * and FinishButton share state.
 */
const MatterExistsLayout: React.FC<{ matterId: string; phase: string }> = ({
  matterId,
  phase,
}) => {
  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const { state } = useUnifiedFlow();
  const isReviewStep = useMemo(
    () => state.currentStepIndex === UNIFIED_STEPS.length - 1,
    [state.currentStepIndex],
  );

  // Check if the automated_introduction workflow is enabled for this matter
  const { data: matterServices } = useGetMatterServicesQuery(
    { matterId },
    { skip: !isReviewStep },
  );
  const isAutoIntroEnabled = useMemo(() => {
    if (!matterServices) return false;
    return matterServices.some((svc) =>
      svc.is_active &&
      svc.workflows.some(
        (w) => w.workflow_code === 'automated_introduction' && w.is_enabled,
      ),
    );
  }, [matterServices]);

  const content = phase === 'creation' ? (
    <CreationStepContent />
  ) : (
    <IngestionStepContent matterId={matterId} firmId={matter?.firm.id ?? ''} />
  );

  // Wrap in messaging provider on review step so FinishButton + panel share state,
  // but only when the automated_introduction workflow is enabled for this matter.
  if (isReviewStep && matterId && isAutoIntroEnabled) {
    return (
      <CaseSetupMessagingProvider matterId={matterId}>
        <PageShell showFinish>
          {content}
        </PageShell>
      </CaseSetupMessagingProvider>
    );
  }

  return (
    <PageShell showFinish>
      {content}
    </PageShell>
  );
};

/**
 * Unified matter creation + ingestion page.
 *
 * Single route with 10-step flow (0-9): 1 creation step + 9 ingestion steps.
 * Wraps all content in UnifiedFlowProvider for state management.
 *
 * @example
 * // In router
 * <Route path="/matters/ingestion" element={<CaseIngestionPage />} />
 * <Route path="/matters/:matterId/ingestion" element={<CaseIngestionPage />} />
 */
export const CaseIngestionPage: React.FC = () => {
  return (
    <UnifiedFlowProvider>
      <UnifiedFlowContent />
    </UnifiedFlowProvider>
  );
};
